import { Live } from '@/app/entities/Live'
import { useLiveMutations } from '@/app/hooks/useLiveMutations'
import { fetchStringToFile } from '@/app/utils/fetchStringToFile'
import { Button } from '@/view/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/view/components/ui/form'
import { Input } from '@/view/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/view/components/ui/select'
import { Spinner } from '@/view/components/ui/spinner'
import { Switch } from '@/view/components/ui/switch'
import { Textarea } from '@/view/components/ui/textarea'
import { InputMask } from '@react-input/mask'
import { Trash2 } from 'lucide-react'
import { useFieldArray } from 'react-hook-form'
import { useEffect } from 'react'
import { extractFileExtension } from '../../ClassDetails/components/helpers'
import { ThumnailDropzone } from '../../ClassDetails/components/ThumbnailDropzone'
import useLiveForm from '../../hooks/useLiveForm'
import { useLiveFormContext } from '../../providers/LiveFormProvider'
import { IconName, IconPicker } from '@/view/components/IconPicker'

export const daysOfWeek = [
  {
    name: 'Todo domingo',
    value: 1,
  },
  {
    name: 'Toda segunda-feira',
    value: 2,
  },
  {
    name: 'Toda terça-feira',
    value: 3,
  },
  {
    name: 'Toda quarta-feira',
    value: 4,
  },
  {
    name: 'Toda quinta-feira',
    value: 5,
  },
  {
    name: 'Toda sexta-feira',
    value: 6,
  },
  {
    name: 'Todo sábado',
    value: 7,
  },
]

const parseLiveToFormValues = async (live: Live) => {
  let thumbnail: File | null = null

  try {
    if (live.thumbnail_url && live.thumbnail_url.trim() !== '') {
      const fetchPromise = fetchStringToFile(
        live.thumbnail_url,
        extractFileExtension(live.thumbnail_url),
        live.thumbnail_url,
      )

      thumbnail = await fetchPromise
      console.log('Thumbnail carregada com sucesso')
    } else {
      console.log('Nenhuma thumbnail_url fornecida')
    }
  } catch (error) {
    console.error('Erro ao carregar thumbnail:', error)
    thumbnail = null
  }

  const result = {
    title: live.title,
    link: live.link,
    description: live.description,
    file: thumbnail,
    icon: live.icon || '',
    frequency: live.frequency,
    isSellersApp: live.isSellersApp || false,
    isTeamsApp: live.isTeamsApp || false,
  }

  return result
}

export const DataStep = () => {
  const { selectedLive, closeCreateLiveModal, isLiveModalOpen } =
    useLiveFormContext()

  const isEditing = !!selectedLive

  const form = useLiveForm({
    getDefaultValue: isEditing
      ? () => parseLiveToFormValues(selectedLive)
      : undefined,
  })

  const isLoadingForm = form.formState.isLoading

  const { control } = form

  const { createLive, updateLive } = useLiveMutations()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'frequency',
  })

  useEffect(() => {
    if (!isLiveModalOpen) {
      form.reset()
    }
  }, [isLiveModalOpen, form])

  const handleSumit = form.handleSubmit(async (data) => {
    try {
      if (isEditing) {
        await updateLive.mutateAsync({ id: selectedLive._id, data })
      } else {
        await createLive.mutateAsync(data)
      }
      form.reset()
      closeCreateLiveModal()
    } catch (error) {
      console.error(error)
    }
  }, console.error)

  const description = form.watch('description')
  const isPending = createLive.isPending || updateLive.isPending

  if (isLoadingForm) {
    return <Spinner />
  }

  return (
    <Form {...form}>
      <form className="mt-8 space-y-4" onSubmit={handleSumit}>
        <div className="flex gap-2">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="grow">
                <FormLabel>Título</FormLabel>
                <FormControl>
                  <Input placeholder="titulo da transmissão" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="icon"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Ícone</FormLabel>
                <FormControl>
                  <IconPicker
                    value={field.value as IconName}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="link"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Link da transmissão</FormLabel>
              <FormControl>
                <Input placeholder="Insira a url" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descrição</FormLabel>
              <FormControl>
                <Textarea placeholder="Descrição da transmissão" {...field} />
              </FormControl>
              <FormMessage />
              <div className="flex justify-end">
                <span className="text-sm text-muted-foreground">
                  {description.length}/500
                </span>
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="file"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Thumbnail</FormLabel>
              <FormControl>
                <ThumnailDropzone
                  value={field.value || undefined}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormLabel>Frequência</FormLabel>
        <div className="flex max-h-[200px] flex-col gap-2 overflow-y-auto">
          {fields?.map((field, index) => (
            <div className="flex gap-2" key={field.id}>
              <FormField
                control={form.control}
                name={`frequency.${index}.day`}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormControl>
                      <Select
                        value={field.value.toString()}
                        onValueChange={(value) => {
                          field.onChange(Number(value))
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o dia" />
                        </SelectTrigger>

                        <SelectContent>
                          {daysOfWeek.map((day) => (
                            <SelectItem
                              key={day.value}
                              value={day.value.toString()}
                            >
                              {day.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`frequency.${index}.hour`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <InputMask
                        mask="xx:xx"
                        replacement={{ x: /[0-9]/ }}
                        component={Input}
                        placeholder="00:00"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  if (fields.length === 1) return
                  remove(index)
                }}
                className="h-10"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          className="w-full rounded-full"
          variant="outline"
          onClick={() => append({ day: 1, hour: '00:00' })}
          disabled={isPending}
        >
          Adicionar frequência
        </Button>

        <FormField
          control={form.control}
          name="isSellersApp"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Vendedores</FormLabel>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isTeamsApp"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Líderes</FormLabel>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />

        <Button
          className="w-full rounded-full text-white"
          type="submit"
          disabled={isPending}
        >
          {isEditing ? 'Atualizar transmissão' : 'Criar transmissão'}
        </Button>
      </form>
    </Form>
  )
}
